import React from 'react';

import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import Button from '@/lib/components/Button';
import { getCountriesList } from '@/lib/utils/countryUtils';
import FormField from '@/lib/components/FormField';
import { useFormSave } from '@/lib/hooks/useFormSave';

import styles from '@/lib/components/PspComposer/PspComposer.module.scss';
import internationalTransfersMexicoStyles from './InternationalTransfersMexico.module.scss';
/**
 * This component is one of the multiple available payment method in this project.
 * It represents an international bank transfers from mexico, only used to collect refund request data for now.
 */
const InternationalTransfersMexico = React.forwardRef(
    (
        {
            pspId,
            token,
            paymentRequestId,
            lyriaUrl,
            hidePayButton = false,
            labelPayButton = null,
            onPaymentResult,
        },
        ref,
    ) => {
        const { t, i18n } = useTranslation();

        const countriesList = Object.entries(getCountriesList(i18n.language)).filter(([key]) => key !== 'MX') // exclude Mexico
            .map(([key, value]) => ({ value: value, label: key + ' - ' + value }));

        const formRef = React.useRef(null);
        const [formData, setFormData] = React.useState({
            'first_name': '',
            'last_name': '',
            'date_of_birth': '',
            'country': '',
            'phone_number': '',
            'account_number': '',
            'bank_account_type': '',
            'swift_code': '',
            'bank_name': '',
            'zipcode': '',
            'city': '',
            'province': '',
            'address': '',
        });

        const [errors, setErrors] = React.useState({});

        const handleSuccess = React.useCallback(
            (response) => {
                onPaymentResult({ success: true, data: response });
            },
            [onPaymentResult],
        );

        const handleError = React.useCallback(
            (response) => {
                if (response?.data) {
                    const fieldErrors = {};
                    Object
                        .keys(response.data)
                        .forEach((key) => {
                            fieldErrors[key] = response.data[key] && response.data[key].map((error) => t([
                                `error_${error.code}`,
                                'error_form',
                            ]));
                        });
                    setErrors(fieldErrors);
                }
                onPaymentResult({
                    success: false,
                    data: response,
                });
            },
            [onPaymentResult, t],
        );

        const url = lyriaUrl + '/international-transfers-mexico/save';
        const saveForm = useFormSave(url, token, paymentRequestId, pspId, handleSuccess, handleError);

        const submitForm = React.useCallback(
            (event) => {
                event.preventDefault();
                event.stopPropagation();
                saveForm(formData);
            },
            [formData, saveForm],
        );

        const onChangeFormField = React.useCallback(
            (fieldName) => (event, newValue) => {
                setFormData((prevState) => ({
                    ...prevState,
                    [fieldName]: newValue,
                }));

            },
            [],
        );

        React.useImperativeHandle(
            ref,
            () => ({
                pay() {
                    return formRef.current.click();
                },
                canClickOnPay: Object.values(formData).every((value) => !!value),
            }),
            [formData],
        );

        return (
            <div id="wz-lyriapay__international-bank" className={styles.common_psp}>
                <form noValidate onSubmit={submitForm}>
                    <div className={styles.common_psp_form}>
                        <span className='credit_input_fields_block_descriptor'>
                            {t('international_transfers_mexico_user_information')}
                        </span>
                        <div className={`${styles.common_field_group} ${internationalTransfersMexicoStyles.bank_fields_wrapper}`}>
                            <FormField
                                name="first_name"
                                value={formData?.first_name}
                                onChange={onChangeFormField('first_name')}
                                label={t('international_transfers_mexico_first_name')}
                                errors={errors.first_name}
                                required
                            />
                            <FormField
                                name="last_name"
                                value={formData?.lastName}
                                onChange={onChangeFormField('last_name')}
                                label={t('international_transfers_mexico_last_name')}
                                errors={errors.last_name}
                                required
                            />
                            <FormField
                                name="date_of_birth"
                                type='date'
                                value={formData?.date_of_birth}
                                onChange={onChangeFormField('date_of_birth')}
                                label={t('international_transfers_mexico_date_of_birth')}
                                errors={errors.date_of_birth}
                                required
                            />
                            <FormField
                                name="country"
                                value={formData?.country}
                                onChange={onChangeFormField('country')}
                                label={t('international_transfers_mexico_country')}
                                errors={errors.country}
                                required
                                type="select"
                                options={countriesList}
                            />
                            <FormField
                                name="phone_number"
                                type='tel'
                                value={formData?.phone_number}
                                onChange={onChangeFormField('phone_number')}
                                label={t('international_transfers_mexico_phone_number')}
                                errors={errors.phone_number}
                                required
                            />
                        </div>
                        <span>
                            {t('international_transfers_mexico_bank_information')}
                        </span>
                        <div className={`${styles.common_field_group} ${internationalTransfersMexicoStyles.bank_fields_wrapper}`}>
                            <FormField
                                name="bank_name"
                                value={formData?.bank_name}
                                onChange={onChangeFormField('bank_name')}
                                label={t('international_transfers_mexico_bank_name')}
                                errors={errors.bank_name}
                                required
                            />
                            <FormField
                                name="bank_account_type"
                                value={formData?.bank_account_type}
                                onChange={onChangeFormField('bank_account_type')}
                                label={t('international_transfers_mexico_bank_account_type')}
                                errors={errors.bank_account_type}
                                required
                                type="select"
                                options={[
                                    { value: '', label: '' },
                                    { value: 'Savings', label: 'Savings' },
                                    { value: 'Checking account', label: 'Checking account' },
                                ]}
                            />
                            <FormField
                                name="account_number"
                                value={formData?.account_number}
                                onChange={onChangeFormField('account_number')}
                                label={t('international_transfers_mexico_account_number')}
                                errors={errors.account_number}
                                required
                            />
                            <FormField
                                name="swift_code"
                                value={formData?.swift}
                                onChange={onChangeFormField('swift_code')}
                                label={t('international_transfers_mexico_swift_code')}
                                errors={errors.swift_code}
                                required
                            />
                        </div>
                        <span>
                            {t('international_transfers_mexico_bank_address')}
                        </span>
                        <div className={`${styles.common_field_group} ${internationalTransfersMexicoStyles.bank_fields_wrapper}`}>
                            <FormField
                                name="city"
                                value={formData?.city}
                                onChange={onChangeFormField('city')}
                                label={t('international_transfers_mexico_city')}
                                errors={errors.city}
                                required
                            />
                            <FormField
                                name="province"
                                value={formData?.province}
                                onChange={onChangeFormField('province')}
                                label={t('international_transfers_mexico_province')}
                                errors={errors.province}
                                required
                            />
                            <FormField
                                name="address"
                                value={formData?.address}
                                onChange={onChangeFormField('address')}
                                label={t('international_transfers_mexico_address')}
                                errors={errors.address}
                                required
                            />
                            <FormField
                                name="zipcode"
                                value={formData?.zipcode}
                                onChange={onChangeFormField('zipcode')}
                                label={t('international_transfers_mexico_zipcode')}
                                errors={errors.zipcode}
                                required
                            />
                        </div>
                    </div>
                    {!hidePayButton && (
                        <div className={styles.common_psp_submit}>
                            <Button
                                disabled={Object.values(formData).some((value) => !value)}
                                label={labelPayButton || t('payButton')}
                                ref={formRef}
                                type='submit'
                            />
                        </div>
                    )}
                </form>
            </div>
        );
    },
);

InternationalTransfersMexico.propTypes = {
    /**
     * The is of the PSP returned by the backend in order to be able to pass it when we submit the form data.
     */
    pspId: PropTypes.number.isRequired,
    /**
     * The token to use in order to be able to call the backend when we submit the form data.
     */
    token: PropTypes.string,
    /**
     * The paymentRequestID to use in order to be able to call the backend when we submit the data in the paymentPage.
     */
    paymentRequestId: PropTypes.string,
    /**
     * The backend url to use to pass the form data.
     */
    lyriaUrl: PropTypes.string.isRequired,
    /**
     * Whether the submit button must be displayed or not.
     */
    hidePayButton: PropTypes.bool,
    /**
     * The label of the 'pay' button to display. hidePayButton props must be false of course.
     */
    labelPayButton: PropTypes.string,
    /**
     * The callback function to trigger when the backend return the result of the form data submit.
     */
    onPaymentResult: PropTypes.func.isRequired,
};

InternationalTransfersMexico.displayName = 'InternationalTransfersMexico';

export default InternationalTransfersMexico;
